// =====================================================================
// SecuriteMesVerifications.jsx — « Mes vérifications » (23-10-2026)
// =====================================================================
// Ce que la personne voit sur son téléphone : UNIQUEMENT les vérifications qui lui
// sont confiées (une par tournée), en gros. Le reste de la tournée ne la regarde pas.
// Un appui ouvre la vérification (SecuriteVerificationLigne) ; à la fin, le temps
// passé est déjà dans ses tâches.
// =====================================================================

import { useState, useEffect, useCallback } from 'react';
import { safetyRoutines, errMsg } from '../services/api.js';
import { useAuth } from '../hooks/useAuth.jsx';
import { duree } from '../lib/tournee.js';
import SecuriteVerificationLigne from './SecuriteVerificationLigne.jsx';

const C = { line: '#e5e7eb', ink: '#3f4654', mute: '#7b8494', brand: '#714B67', ok: '#16a34a', okBg: '#effaf3', crit: '#dc2626', critBg: '#fdf0f0', warn: '#d97706' };
const GROUPES = [['EN_RETARD', 'En retard', C.crit], ['AUJOURDHUI', 'Aujourd’hui', C.brand], ['A_VENIR', 'À venir', C.mute]];
const TEMPS = {
  SAISI: 'Temps ajouté à vos tâches.',
  VERROUILLE: 'Période clôturée : temps à saisir à la main.',
  NON_MESURE: 'Temps non mesuré (pas de chronomètre ni de durée habituelle).',
  SANS_PROJET: 'Pas de tâche prévue pour ce temps : à saisir à la main.',
  TROP_LONG: 'Plus de 4 h entre Commencer et Terminé (oubli ?) : temps à saisir à la main.',
};

export default function SecuriteMesVerifications() {
  const { user } = useAuth();
  const [liste, setListe] = useState(null);
  const [ouverte, setOuverte] = useState(null);
  const [lang, setLang] = useState('fr');
  const [flash, setFlash] = useState(null);
  const [err, setErr] = useState(null);

  const charger = useCallback(async () => {
    try { setListe(await safetyRoutines.mesVerifications()); setErr(null); } catch (e) { setErr(errMsg(e)); }
  }, []);
  useEffect(() => { charger(); }, [charger]);

  const titre = (l) => (lang === 'ro' && l.labelRo) || (lang === 'en' && l.labelEn) || l.label;

  if (ouverte) {
    return (
      <SecuriteVerificationLigne ligne={ouverte} moi={user?.id} lang={lang}
        onRetour={() => { setOuverte(null); charger(); }}
        onFini={(r) => {
          const actions = (r.actions || []).filter((a) => !a.reused).map((a) => a.ref);
          setFlash(`Vérification enregistrée${r.dureeMin ? ` (${duree(r.dureeMin)}${r.estimee ? ', durée habituelle' : ''})` : ''}. `
            + `${TEMPS[r.temps?.statut] || ''}${actions.length ? ` Action corrective ouverte : ${actions.join(', ')}.` : ''}`
            + `${r.cloture ? ' La tournée est complète.' : ''}`);
          setOuverte(null); charger();
        }} />
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginBottom: 10 }}>
        {['fr', 'ro', 'en'].map((k) => (
          <button key={k} type="button" onClick={() => setLang(k)} style={{
            border: `1px solid ${C.line}`, borderRadius: 8, padding: '6px 12px', fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer',
            background: lang === k ? C.brand : '#fff', color: lang === k ? '#fff' : C.ink,
          }}>{k.toUpperCase()}</button>
        ))}
      </div>
      {err && <div style={{ background: C.critBg, color: '#991B1B', borderRadius: 10, padding: '0.8rem', marginBottom: 10 }}>{err}</div>}
      {flash && <div style={{ background: C.okBg, color: '#065F46', borderRadius: 10, padding: '0.8rem', marginBottom: 10, fontWeight: 600 }}>{flash}</div>}
      {liste && liste.length === 0 && (
        <div style={{ background: '#fff', border: `1px solid ${C.line}`, borderRadius: 12, padding: '1.4rem', textAlign: 'center', color: C.mute, fontSize: '1.05rem' }}>
          Aucune vérification ne vous est confiée pour le moment.
        </div>
      )}
      {liste && GROUPES.map(([k, nom, col]) => {
        const g = liste.filter((l) => l.quand === k);
        if (!g.length) return null;
        return (
          <div key={k} style={{ marginBottom: 14 }}>
            <div style={{ fontWeight: 800, color: col, margin: '4px 0 8px', fontSize: '0.95rem', textTransform: 'uppercase', letterSpacing: '.04em' }}>{nom}</div>
            {g.map((l) => (
              <button key={`${l.occurrenceId}-${l.itemId}`} type="button" onClick={() => setOuverte(l)} style={{
                display: 'block', width: '100%', textAlign: 'left', background: '#fff', cursor: 'pointer',
                border: `2px solid ${l.faite ? '#cfead9' : (k === 'EN_RETARD' ? '#f6cfcf' : C.line)}`, borderRadius: 12,
                padding: '1rem', marginBottom: 10, minHeight: 72,
              }}>
                <div style={{ fontSize: '1.1rem', fontWeight: 700, color: C.ink }}>{titre(l)}</div>
                <div style={{ fontSize: '0.88rem', color: C.mute, marginTop: 4 }}>
                  {l.templateName} · {String(l.dueDate).slice(0, 10)}
                  {l.avec.length > 1 && <> · avec {l.avec.filter((p) => p.id !== user?.id).map((p) => p.name).join(', ')}</>}
                </div>
                <div style={{ fontSize: '0.92rem', fontWeight: 700, marginTop: 6, color: l.faite ? C.ok : (l.commenceeA ? C.warn : C.brand) }}>
                  {l.faite ? `✓ Faite (${l.reponse})${l.dureeMin ? ` — ${duree(l.dureeMin)}` : ''}` : (l.commenceeA ? '⏱ En cours — touchez pour terminer' : 'À faire — touchez pour commencer')}
                </div>
              </button>
            ))}
          </div>
        );
      })}
    </div>
  );
}
